import React from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { ArrowLeft, FlaskConical, Lock, Globe2 } from "lucide-react";

// HAVEN Deal — Phase 2 product surface (SCAFFOLD, flag-gated).
// Renders only when REACT_APP_HAVEN_DEAL_ENABLED is true; otherwise redirects to /app.
// No solver-panel content is shown until a classical verdict exists — this scaffold
// deliberately shows the structure + a "verdict pending" placeholder only.
const FLAG = String(process.env.REACT_APP_HAVEN_DEAL_ENABLED || "").toLowerCase() === "true";

const C = {
  bg: "#0b0f14", panel: "#111823", border: "#1f2b3a", text: "#e6edf3",
  muted: "#8b9bb0", accent: "#4cc9f0", warn: "#f4a259",
};

function Card({ children, testid, style }) {
  return (
    <div data-testid={testid} style={{
      background: C.panel, border: `1px solid ${C.border}`, borderRadius: 14,
      padding: "24px 28px", ...style,
    }}>{children}</div>
  );
}

export default function HavenDeal() {
  const navigate = useNavigate();
  const [verdict, setVerdict] = React.useState(null);   // null=loading, {status,...}
  const [phase4, setPhase4] = React.useState(null);
  React.useEffect(() => {
    if (!FLAG) return;
    const base = process.env.REACT_APP_BACKEND_URL || "";
    fetch(`${base}/api/haven/deal/verdict?campaign=haven_deal_phase2_v2`)
      .then((r) => (r.ok ? r.json() : { status: "pending" }))
      .then(setVerdict)
      .catch(() => setVerdict({ status: "pending" }));
    fetch(`${base}/api/haven/deal/phase4/status`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setPhase4)
      .catch(() => setPhase4(null));
  }, []);
  if (!FLAG) return <Navigate to="/app" replace />;

  const ready = verdict && verdict.status === "ready";
  const interim = verdict && verdict.status === "interim";
  const dealClaim = phase4 && (phase4.claim_index?.entries || []).find((e) => e.claim === "haven_deal_quantum");

  return (
    <div data-testid="haven-deal-page" style={{ minHeight: "100vh", background: C.bg, color: C.text,
      fontFamily: "ui-sans-serif, system-ui, -apple-system, sans-serif", padding: "40px 6vw" }}>
      <button data-testid="haven-deal-back" onClick={() => navigate("/app/optimization")}
        style={{ display: "flex", alignItems: "center", gap: 8, background: "transparent",
          border: `1px solid ${C.border}`, color: C.muted, borderRadius: 999, padding: "8px 16px",
          cursor: "pointer", fontSize: 13, marginBottom: 32 }}>
        <ArrowLeft size={15} /> Optimization
      </button>

      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
        <FlaskConical size={26} color={C.accent} />
        <h1 style={{ fontSize: 34, fontWeight: 700, margin: 0, letterSpacing: "-0.02em" }}>HAVEN Deal</h1>
        <span data-testid="haven-deal-phase-badge" style={{ fontSize: 11, textTransform: "uppercase",
          letterSpacing: "0.08em", color: C.warn, border: `1px solid ${C.warn}`, borderRadius: 999,
          padding: "3px 10px", fontWeight: 600 }}>Phase 2 · development</span>
      </div>
      <p style={{ color: C.muted, maxWidth: 720, fontSize: 15, lineHeight: 1.6, marginBottom: 36 }}>
        Quantum-native asset-acquisition decision (metro × entry window, property type post-selected),
        run against the full classical portfolio at the same scale. The moat is the decision
        architecture and the evidence — quantum is a measured option, not an assumption.
      </p>

      <div style={{ display: "grid", gap: 20, gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
        <Card testid="haven-deal-structure-card">
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
            <Globe2 size={17} color={C.accent} />
            <span style={{ fontSize: 13, textTransform: "uppercase", letterSpacing: "0.06em", color: C.muted }}>Instance structure</span>
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 6 }}>z<sub>m,t</sub> · n = 120–252</div>
          <div style={{ color: C.muted, fontSize: 13.5, lineHeight: 1.6 }}>
            Real FHFA metro co-movement (S) × window kernel (W). Genuinely indefinite
            (neg-eig 0.6–0.7), full rank, no padding. Property type post-selected; CoStar
            type co-movement unavailable and recorded, never fabricated.
          </div>
        </Card>

        <Card testid="haven-deal-verdict-pending-card" style={{ borderStyle: ready ? "solid" : "dashed" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
            <Lock size={17} color={ready ? C.accent : C.warn} />
            <span style={{ fontSize: 13, textTransform: "uppercase", letterSpacing: "0.06em", color: C.muted }}>Solver comparison</span>
          </div>
          {ready || interim ? (
            <>
              <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 10, color: interim ? C.warn : C.accent }}>
                {interim ? "Classical verdict (INTERIM — grid still running)" : "Classical verdict ready"}
              </div>
              <pre data-testid="haven-deal-verdict-md" style={{ whiteSpace: "pre-wrap", color: C.text,
                fontSize: 12.5, lineHeight: 1.55, maxHeight: 260, overflow: "auto", margin: 0 }}>
                {verdict.verdict_md}
              </pre>
            </>
          ) : (
            <>
              <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 6, color: C.warn }}>Classical verdict pending</div>
              <div style={{ color: C.muted, fontSize: 13.5, lineHeight: 1.6 }}>
                The solver panel stays empty until the classical checkpoint campaign completes and
                a measured verdict exists. No quantum claim, no arm figures, and no Leap submission
                are shown before then.
              </div>
            </>
          )}
        </Card>
      </div>

      {phase4 && (
        <Card testid="haven-deal-phase4-card" style={{ marginTop: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
            <Lock size={17} color={C.accent} />
            <span style={{ fontSize: 13, textTransform: "uppercase", letterSpacing: "0.06em", color: C.muted }}>
              Phase 4 · production solver &amp; claim staging
            </span>
          </div>
          <div data-testid="haven-deal-production-solver" style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>
            Production solver: <span style={{ color: C.accent }}>best classical arm</span>
            <span style={{ color: C.muted, fontWeight: 400 }}> · D-Wave arm gated (enabled per tier only when the claim is <code>confirmed</code>)</span>
          </div>
          <div data-testid="haven-deal-claim-state" style={{ color: C.muted, fontSize: 13.5, lineHeight: 1.7 }}>
            <div>Claim <code style={{ color: C.text }}>haven_deal_quantum</code>: <b style={{ color: C.warn }}>{dealClaim ? dealClaim.state : "not_demonstrated"}</b>
              {dealClaim && dealClaim.phase4_no_op_reason ? ` (${dealClaim.phase4_no_op_reason})` : ""}</div>
            <div>Live submission: <b>{phase4.live_submission_enabled ? "enabled" : "dry-run ($0, no Leap)"}</b> ·
              preflight ready: <b>{String(phase4.preflight?.ready_for_live)}</b> ·
              superiority phrasing permitted: <b>{String(phase4.claim_index?.quantum_advantage_language_permitted)}</b></div>
          </div>
        </Card>
      )}

      <div style={{ marginTop: 40, color: C.muted, fontSize: 12.5 }} data-testid="haven-deal-flag-note">
        Behind <code style={{ color: C.accent }}>HAVEN_DEAL_ENABLED</code> · D-Wave not_run · no Leap submission.
      </div>
    </div>
  );
}
